import React, { useState, useEffect, useCallback } from 'react';
import { Box, Modal, Typography, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReviewCard from './ReviewCard';
import './PendingRepliesModal.css';
import { axiosInstance } from '../../../utils/tokenUtils';
import { format, parseISO, addMinutes } from 'date-fns';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '8px',
  width: '1000px',
  maxWidth: '90vw',
  padding: '24px',
};

const mapSessionType = type => {
  const typeMap = {
    one_on_one: 'One-On-One',
    group: 'Group Session',
    prime: 'Prime',
  };
  return typeMap[type] || type;
};

const mapSchoolLevel = level => {
  const levelMap = {
    elementary: 'Elementary School',
    middle_school: 'Middle School',
    high_school: 'High School',
    college: 'College',
    university: 'University',
  };
  return levelMap[level] || level;
};

const PendingRepliesModal = ({ open, onClose, onRefreshInsights }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasReplied, setHasReplied] = useState(false);
  const [instructorProfile, setInstructorProfile] = useState(null);

  const transformReviewData = apiReview => {
    const startTime = parseISO(apiReview.session.start_time);
    const endTime = addMinutes(startTime, apiReview.session.length_in_minutes);

    return {
      id: apiReview.id,
      learner: {
        name: `${apiReview.student_profile.first_name} ${apiReview.student_profile.last_name}`,
        profile_image: apiReview.student_profile.profile_image || '',
        school_level: mapSchoolLevel(apiReview.session.level_of_teaching),
      },
      session: {
        subject: apiReview.session.subject,
        date: format(startTime, 'EEE d MMM, yyyy'),
        timing: `${format(startTime, 'h:mm a')} - ${format(endTime, 'h:mm a')}`,
        type: mapSessionType(apiReview.session.session_type),
      },
      rating: apiReview.rating,
      review_text: apiReview.feedback,
      teacher_response: apiReview.teacher_response,
      responded_at: apiReview.responded_at,
    };
  };

  const fetchInstructorProfile = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/users/me');
      setInstructorProfile({
        name: `${response.data.first_name} ${response.data.last_name}`,
        image: response.data.profile_image || '',
      });
    } catch (err) {
      console.error('Error fetching instructor profile:', err);
    }
  }, []);

  const fetchPendingReviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/teachers/reviews', {
        params: {
          offset: 0,
          limit: 20,
        },
      });
      const transformedReviews = response.data.results.map(transformReviewData);
      setReviews(transformedReviews);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      // Check if error is 404 - no pending replies found
      if (err?.response?.status === 404) {
        setError('No Pending Replies Found');
      } else {
        setError('Failed to load reviews. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleReplySuccess = reviewId => {
    setHasReplied(true);
    fetchPendingReviews();
  };

  const handleClose = () => {
    if (hasReplied && onRefreshInsights) {
      onRefreshInsights();
    }
    setHasReplied(false);
    onClose();
  };

  useEffect(() => {
    if (open) {
      fetchInstructorProfile();
      fetchPendingReviews();
    }
  }, [open, fetchInstructorProfile, fetchPendingReviews]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Box className="pending-replies-modal-header">
          <Typography className="pending-replies-title">Pending Replies</Typography>
          <IconButton onClick={handleClose} className="pending-replies-close-button">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="pending-replies-content">
          {loading ? (
            <Box className="pending-replies-loading">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography className="pending-replies-error">{error}</Typography>
          ) : reviews.length === 0 ? (
            <Typography className="pending-replies-empty">
              No pending reviews to reply to.
            </Typography>
          ) : (
            reviews.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                onReplySuccess={handleReplySuccess}
                instructorProfile={instructorProfile}
              />
            ))
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default PendingRepliesModal;
